import React, { useEffect, useRef } from 'react';
import { fitClipsToSources, type EditorState } from '@rehub/propertycut-timeline';
import { probeVideoUrlDuration, saveAssetDuration } from '../../services/supabase';
import type { PropertyCutAsset } from '../../types';

/** Lengths within this of each other are treated as the same. */
const TOLERANCE_SECONDS = 0.05;

/**
 * Measures every video's real length in the browser, saves it when the
 * stored length is missing or wrong, and shortens clips that would run
 * past the end of their footage. Each asset is measured once per session.
 */
export function useVideoLengths(opts: {
  assets: PropertyCutAsset[];
  setAssets: React.Dispatch<React.SetStateAction<PropertyCutAsset[]>>;
  sync: (recipe: (current: EditorState) => EditorState) => void;
}) {
  const { assets, setAssets, sync } = opts;
  const probed = useRef(new Set<string>());

  useEffect(() => {
    for (const asset of assets) {
      if (asset.kind !== 'video' || !asset.signed_url || probed.current.has(asset.id)) continue;
      probed.current.add(asset.id);
      void probeVideoUrlDuration(asset.signed_url).then((seconds) => {
        if (!seconds) return;
        const stored = asset.duration_seconds;
        if (stored != null && Math.abs(stored - seconds) <= TOLERANCE_SECONDS) return;
        setAssets((current) =>
          current.map((item) => (item.id === asset.id ? { ...item, duration_seconds: Math.round(seconds * 100) / 100 } : item)),
        );
        void saveAssetDuration(asset.id, seconds).catch(() => undefined);
      });
    }
  }, [assets, setAssets]);

  useEffect(() => {
    const lengths = new Map<string, number>();
    for (const asset of assets) {
      if (asset.kind === 'video' && asset.duration_seconds) lengths.set(asset.id, asset.duration_seconds);
    }
    if (lengths.size > 0) sync((current) => fitClipsToSources(current, lengths));
  }, [assets, sync]);
}
